(() => {
  const storageKey = 'nw-rap-wishlist';

  const config = () => window.NWRap || {};

  const rootUrl = () => {
    const root = config().rootUrl || window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : root + '/';
  };

  const readWishlist = () => {
    try {
      const value = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
      return Array.isArray(value) ? [...new Set(value.filter((handle) => typeof handle === 'string' && handle.length))] : [];
    } catch {
      return [];
    }
  };

  const writeWishlist = (handles) => {
    window.localStorage.setItem(storageKey, JSON.stringify([...new Set(handles)]));
    window.dispatchEvent(new CustomEvent('nw-rap:wishlist-change'));
  };

  const escapeHtml = (value = '') =>
    String(value).replace(/[&<>"']/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    })[character]);

  const formatMoney = (value) => {
    const locale = config().locale || document.documentElement.lang || 'de-DE';
    const currency = config().currency || 'EUR';
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(Number(value || 0) / 100);
  };

  const wishlistDialog = () => document.querySelector('[data-nw-wishlist-dialog]');

  const syncWishlistButtons = () => {
    const handles = readWishlist();
    document.querySelectorAll('[data-nw-wishlist-toggle]').forEach((button) => {
      const active = handles.includes(button.dataset.handle);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', active ? button.dataset.labelRemove : button.dataset.labelAdd);
      const label = button.querySelector('[data-nw-wishlist-label]');
      if (label) label.textContent = active ? button.dataset.labelRemove : button.dataset.labelAdd;
    });

    document.querySelectorAll('[data-nw-wishlist-count]').forEach((count) => {
      count.textContent = handles.length;
      count.hidden = handles.length === 0;
    });
  };

  const renderWishlist = async () => {
    const list = document.querySelector('[data-nw-wishlist-list]');
    if (!list) return;

    const handles = readWishlist();
    if (handles.length === 0) {
      list.innerHTML = '<p class="nw-rap-wishlist__empty">' + escapeHtml(list.dataset.emptyText) + '</p>';
      return;
    }

    list.setAttribute('aria-busy', 'true');
    const products = await Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(rootUrl() + 'products/' + encodeURIComponent(handle) + '.js', {
            headers: { Accept: 'application/json' },
          });
          if (!response.ok) throw new Error('Product unavailable');
          return await response.json();
        } catch {
          return null;
        }
      }),
    );

    const availableProducts = products.filter(Boolean);
    const invalidHandles = handles.filter((handle, index) => !products[index]);
    if (invalidHandles.length) writeWishlist(handles.filter((handle) => !invalidHandles.includes(handle)));

    if (availableProducts.length === 0) {
      list.innerHTML = '<p class="nw-rap-wishlist__empty">' + escapeHtml(list.dataset.emptyText) + '</p>';
      list.removeAttribute('aria-busy');
      return;
    }

    list.innerHTML = availableProducts
      .map((product) => {
        const image = product.featured_image || product.images?.[0] || '';
        const price = product.price ? '<span>' + formatMoney(product.price) + '</span>' : '';
        return [
          '<article class="nw-rap-wishlist__item">',
          '<a href="' + escapeHtml(product.url) + '" class="nw-rap-wishlist__image">',
          image
            ? '<img src="' + escapeHtml(image) + '" alt="' + escapeHtml(product.title) + '" loading="lazy" width="240" height="300">'
            : '',
          '</a>',
          '<div class="nw-rap-wishlist__details">',
          '<a href="' + escapeHtml(product.url) + '">' + escapeHtml(product.title) + '</a>',
          price,
          '<button type="button" class="nw-rap-text-button" data-nw-wishlist-remove data-handle="' + escapeHtml(product.handle) + '">',
          escapeHtml(list.dataset.removeText),
          '</button>',
          '</div>',
          '</article>',
        ].join('');
      })
      .join('');
    list.removeAttribute('aria-busy');
  };

  const openDialog = (dialog) => {
    if (!dialog) return;
    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
  };

  const closeDialog = (dialog) => {
    if (!dialog) return;
    if (typeof dialog.close === 'function') {
      if (dialog.open) dialog.close();
    } else {
      dialog.removeAttribute('open');
    }
  };

  const initializeDialogs = () => {
    document.querySelectorAll('[data-nw-dialog]').forEach((dialog) => {
      if (dialog.dataset.nwDialogBound === 'true') return;
      dialog.dataset.nwDialogBound = 'true';
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) closeDialog(dialog);
      });
    });
  };

  const renderCrossSell = async (container) => {
    if (!container || container.dataset.loaded === 'true') return;
    container.dataset.loaded = 'true';
    const grid = container.querySelector('[data-nw-cross-sell-grid]');
    if (!grid || !container.dataset.collectionUrl) return;

    try {
      const response = await fetch(container.dataset.collectionUrl.replace(/\/$/, '') + '/products.json?limit=12', {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Collection unavailable');
      const payload = await response.json();
      const excluded = new Set((container.dataset.cartProductIds || '').split(',').filter(Boolean));
      const products = (payload.products || [])
        .filter((product) => !excluded.has(String(product.id)))
        .map((product) => ({
          ...product,
          variant: product.variants?.find((variant) => variant.available),
        }))
        .filter((product) => product.variant)
        .slice(0, Number(container.dataset.limit || 4));

      if (products.length === 0) {
        container.hidden = true;
        return;
      }

      grid.innerHTML = products
        .map((product) => {
          const image = product.images?.[0]?.src || '';
          const productUrl = rootUrl() + 'products/' + product.handle;
          return [
            '<article class="nw-rap-cross-sell__item">',
            '<a class="nw-rap-cross-sell__image" href="' + escapeHtml(productUrl) + '">',
            image
              ? '<img src="' + escapeHtml(image) + '" alt="' + escapeHtml(product.title) + '" loading="lazy" width="140" height="176">'
              : '',
            '</a>',
            '<div class="nw-rap-cross-sell__copy">',
            '<a href="' + escapeHtml(productUrl) + '">' + escapeHtml(product.title) + '</a>',
            '<span>' + formatMoney(product.variant.price) + '</span>',
            '<button type="button" class="nw-rap-cross-sell__add" data-nw-cross-sell-add data-variant-id="' + product.variant.id + '">',
            escapeHtml(container.dataset.addText),
            '</button>',
            '</div>',
            '</article>',
          ].join('');
        })
        .join('');
    } catch {
      container.hidden = true;
    }
  };

  const refreshCartDrawer = async (variantId) => {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) {
      window.location.assign(rootUrl() + 'cart');
      return;
    }

    const response = await fetch(rootUrl() + '?sections=cart-drawer,cart-icon-bubble', {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Cart refresh unavailable');
    const sections = await response.json();
    cartDrawer.renderContents({ id: variantId, sections });
  };

  const addCrossSellToCart = async (button) => {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;
    button.disabled = true;
    button.classList.add('is-loading');

    try {
      const response = await fetch(rootUrl() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
      if (!response.ok) throw new Error('Add to cart unavailable');
      await refreshCartDrawer(variantId);
    } catch {
      button.disabled = false;
    } finally {
      button.classList.remove('is-loading');
    }
  };

  const initializeCrossSell = () => {
    document.querySelectorAll('[data-nw-cross-sell]').forEach((container) => renderCrossSell(container));
  };

  document.addEventListener('click', (event) => {
    const wishlistButton = event.target.closest('[data-nw-wishlist-toggle]');
    if (wishlistButton) {
      const handles = readWishlist();
      const handle = wishlistButton.dataset.handle;
      if (handles.includes(handle)) {
        writeWishlist(handles.filter((item) => item !== handle));
      } else {
        writeWishlist([...handles, handle]);
      }
      return;
    }

    const removeButton = event.target.closest('[data-nw-wishlist-remove]');
    if (removeButton) {
      writeWishlist(readWishlist().filter((handle) => handle !== removeButton.dataset.handle));
      return;
    }

    if (event.target.closest('[data-nw-wishlist-open]')) {
      renderWishlist();
      openDialog(wishlistDialog());
      return;
    }

    if (event.target.closest('[data-nw-size-open]')) {
      openDialog(document.querySelector('[data-nw-size-dialog]'));
      return;
    }

    const closeButton = event.target.closest('[data-nw-dialog-close]');
    if (closeButton) {
      closeDialog(closeButton.closest('[data-nw-dialog]'));
      return;
    }

    const crossSellButton = event.target.closest('[data-nw-cross-sell-add]');
    if (crossSellButton) addCrossSellToCart(crossSellButton);
  });

  const boot = () => {
    initializeDialogs();
    syncWishlistButtons();
    initializeCrossSell();
  };

  window.addEventListener('nw-rap:wishlist-change', () => {
    syncWishlistButtons();
    renderWishlist();
  });

  window.addEventListener('storage', (event) => {
    if (event.key === storageKey) {
      syncWishlistButtons();
      renderWishlist();
    }
  });

  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('DOMContentLoaded', () => {
    boot();
    const observer = new MutationObserver(initializeCrossSell);
    observer.observe(document.body, { childList: true, subtree: true });
  });
})();
